import moment, { Moment } from 'moment';
import AppState from 'AppState';

/**
 * The default format to use when formatting times. Excludes time zone.
 */
export const DefaultDateFormat = 'MMMM Do YYYY';

/**
 * The default format to use when formatting times. Includes time zone.
 */
export const DefaultTimeFormat = 'h:mm:ss a z';

/**
 * The default format to use when formatting dates-times. Includes time zone.
 */
export const DefaultDateTimeFormat = `${DefaultDateFormat}, ${DefaultTimeFormat}`;

/**
 * Wrapper for moment(...). Returns a moment localized to the server's time zone.
 * If time zone settings are configured improperly, behavior is undefined. The most likely behavior is that the client's time zone will be used.
 * @param inp The time input that would normally be given to moment(...).
 */
export function localMoment(inp?: moment.MomentInput) {
	let t = moment(inp);
	if (AppState.settings.timeZone) {
		t = t.tz(AppState.settings.timeZone);
	} else {
		console.warn(
			"The global time zone setting doesn't exist. Make sure tasks are running, and if that fails, check the Setting table for TimeZone.",
		);
	}
	return t;
}

/**
 * format local moment to GMT timezone format.
 */
export function timeZoneLessLocalMoment(moment: Moment) {
	return moment.format('ddd MMM DD YYYY HH:mm:ss') + ' GMT-0000';
}

/**
 * Returns a function that returns a user friendly, server time zone localized date string when given either a date string or moment time.
 * @param format Overrides the format string to use for formatting the date time. Default: DefaultDateFormat
 */
export function formatDateTimeFunction(format?: string) {
	/** Wrapper for moment(...). Returns a moment localized to the servers time zone. */
	return (inp?: moment.MomentInput) => {
		return formatDateTime(inp, format);
	};
}

/**
 * Converts a moment or date string into a user friendly, server time zone localized date string.
 * @param inp The time input that would normally be given to moment(...).
 * @param format Overrides the format string to use for formatting the date time. Default: DefaultDateFormat
 */
export function formatDateTime(inp?: moment.MomentInput, format?: string): string {
	if (!inp) {
		return '';
	}

	return localMoment(inp || undefined).format(format || DefaultDateTimeFormat);
}
